import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createProcessingFactory } from '#/api/momMgmt/settings/processingFactory/create';
import { fetchProcessingFactoryDetail } from '#/api/momMgmt/settings/processingFactory/fetchDetail';
import { updateProcessingFactory } from '#/api/momMgmt/settings/processingFactory/update';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import ProcessingFactoryAddressesEditor from '#/modules/momMgmt/settings/processingFactory/components/ProcessingFactoryAddressesEditor';
import ProcessingFactoryContactsEditor from '#/modules/momMgmt/settings/processingFactory/components/ProcessingFactoryContactsEditor';
import {
  getProcessingFactoryApiErrorMessage,
  isProcessingFactoryVersionConflict,
} from '#/modules/momMgmt/settings/processingFactory/helper/apiError';
import {
  mapProcessingFactoryDetailToForm,
  prepareProcessingFactoryFormValues,
} from '#/modules/momMgmt/settings/processingFactory/helper/mapProcessingFactoryForm';
import {
  PROCESSING_FACTORY_FIELD_LABELS,
  resolveProcessingFactoryFieldLabel,
} from '#/modules/momMgmt/settings/processingFactory/helper/formValidationLabels';
import {
  PROCESSING_FACTORY_FORM_DEFAULT_VALUES,
  processingFactoryFormSchema,
} from '#/modules/momMgmt/settings/processingFactory/schemas/processingFactorySchema';
import {
  lookupQueryKey,
  processingFactoryDetailQueryKey,
  processingFactoryListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditProcessingFactoryPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: processingFactoryDetailQueryKey(id),
    queryFn: () => fetchProcessingFactoryDetail(id),
    enabled: Boolean(id),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createProcessingFactory,
    updateFn: updateProcessingFactory,
    buildPayload: (values) => {
      const prepared = prepareProcessingFactoryFormValues(values);
      if (id) {
        return {
          ...prepared,
          updatedAt: updatedAtRef.current,
        };
      }
      return prepared;
    },
    listQueryKeyPrefix: processingFactoryListQueryKeyPrefix,
    detailQueryKey: processingFactoryDetailQueryKey,
    getEditPath: ROUTES.processingFactoryEdit,
    getErrorMessage: (error) => getProcessingFactoryApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isProcessingFactoryVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(processingFactoryFormSchema),
    defaultValues: PROCESSING_FACTORY_FORM_DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapProcessingFactoryDetailToForm(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯加工廠' : '新增加工廠';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.processingFactory}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getProcessingFactoryApiErrorMessage(detailQuery.error, '找不到加工廠資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={PROCESSING_FACTORY_FIELD_LABELS}
                fieldLabelResolver={resolveProcessingFactoryFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <FloatingInput
                        id="code"
                        label="代號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="chineseName"
                        label="中文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="englishName"
                        label="英文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="taxIdNumber"
                        label="統編"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="homepage"
                        label="網址"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="phone"
                        label="電話"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="fax"
                        label="傳真"
                        register={register}
                        errors={errors}
                      />
                    </div>
                    <div className="mt-4">
                      <FloatingSelect
                        id="warehouseIds"
                        label="工作倉"
                        options={warehouseOptionsQuery.data ?? []}
                        control={control}
                        errors={errors}
                        isMulti
                        isDisabled={warehouseOptionsQuery.isLoading}
                        noOptionsMessage="無可用倉別"
                      />
                    </div>
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                    />
                  </section>

                  <div className="pb-6">
                    <ProcessingFactoryAddressesEditor
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                    />
                  </div>

                  <div>
                    <ProcessingFactoryContactsEditor
                      control={control}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                    />
                  </div>
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.processingFactory}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditProcessingFactoryPage;
